import { createApi } from '@reduxjs/toolkit/query/react';
import { axiosBaseQuery } from '../baseQuery';
import type { SlotMessage } from '../../components/timetable/SlotMessageThread';
import type { ClassReportData } from '../../components/timetable/ClassReport';

export interface TimetableSlotForTrainer {
	id: string;
	class: any;
	periodTemplate: any;
	school: any;
	dayOfWeek: number;
	dayBucket: string;
	startsAt: string;
	endsAt: string;
	trainers: any[];
	isLabSlot: boolean;
	status: string;
	publishedAt: string | null;
	sessionLog: any | null;
	// Clubbed period grouping. Slots taught together share a bundleId (null for
	// standalone). The today view collapses same-bundle slots into one tile.
	bundleId: string | null;
}

export interface TrainerNotification {
	id: string;
	trainer: string;
	type: string;
	timetableSlot: string | null;
	title: string;
	body: string;
	scheduledFor: string;
	sentAt: string | null;
	emailSent: boolean;
	readAt: string | null;
	createdAt: string;
}

// ── Daily Report (end-of-day) ──────────────────────────────────────

export interface DayReportPhoto {
	id: string;
	url: string;
	capturedAt: string;
}

export interface DayReportEntry {
	slotId: string;
	classLabels: string[];
	timeLabel: string;
	slotStatus: string;
	curriculum: {
		curriculumTitle: string | null;
		moduleTitle: string | null;
		sessionTitle: string | null;
		moduleIndex: number;
		sessionIndex: number;
	} | null;
	outcome: string;
	attendance: { present: number; total: number };
	missedReasonLabel: string | null;
	missedReasonNote: string | null;
	photos: DayReportPhoto[];
	remark: string;
	complete: boolean;
}

export interface DayReportSchool {
	schoolId: string;
	schoolName: string;
	entries: DayReportEntry[];
}

export interface DayReportResponse {
	date: string;
	timezone: string;
	submittedAt: string | null;
	schools: DayReportSchool[];
}

export const trainerTimetableApi = createApi({
	reducerPath: 'trainerTimetableApi',
	baseQuery: axiosBaseQuery,
	tagTypes: ['TrainerToday', 'TrainerWeek', 'TrainerNotifs', 'Continuations', 'SlotMessages', 'MyRequests', 'DayReport'],
	endpoints: (builder) => ({
		getToday: builder.query<
			{ date: string; timezone: string; slots: TimetableSlotForTrainer[] },
			void
		>({
			query: () => ({ url: '/trainer/timetable/today' }),
			providesTags: ['TrainerToday'],
		}),
		getWeek: builder.query<
			{
				weekStartDate?: string;
				startDate?: string;
				endDate?: string;
				timezone: string;
				slots: TimetableSlotForTrainer[];
			},
			{ weekStartDate?: string; startDate?: string; endDate?: string }
		>({
			query: ({ weekStartDate, startDate, endDate }) => ({
				url: '/trainer/timetable/week',
				params: { weekStartDate, startDate, endDate },
			}),
			providesTags: ['TrainerWeek'],
		}),
		startSession: builder.mutation<
			{ slot: any; log: any },
			{
				slotId: string;
				curriculumId?: string;
				moduleIndex?: number;
				sessionIndex?: number;
				isContinuationOf?: string;
			}
		>({
			query: ({ slotId, ...body }) => ({
				url: `/trainer/timetable/slots/${slotId}/start`,
				method: 'POST',
				data: body,
			}),
			invalidatesTags: ['TrainerToday', 'TrainerWeek'],
		}),
		addMilestone: builder.mutation<any, { slotId: string; label: string }>({
			query: ({ slotId, label }) => ({
				url: `/trainer/timetable/slots/${slotId}/milestone`,
				method: 'POST',
				data: { label },
			}),
			invalidatesTags: ['TrainerToday'],
		}),
		endSession: builder.mutation<
			{ slot: any; log: any },
			{
				slotId: string;
				topicsCovered: string[];
				topicsSkipped: string[];
				remarks: string;
				completedEarly: boolean;
				completedEarlyReason?: string;
				spilledOver: boolean;
				curriculumId?: string;
				moduleIndex?: number;
				sessionIndex?: number;
			}
		>({
			query: ({ slotId, ...body }) => ({
				url: `/trainer/timetable/slots/${slotId}/end`,
				method: 'POST',
				data: body,
			}),
			invalidatesTags: ['TrainerToday', 'TrainerWeek'],
		}),
		submitMissedReason: builder.mutation<
			any,
			{ slotId: string; missedReason: string; missedReasonNote?: string }
		>({
			query: ({ slotId, ...body }) => ({
				url: `/trainer/timetable/slots/${slotId}/missed-reason`,
				method: 'POST',
				data: body,
			}),
			invalidatesTags: ['TrainerToday', 'TrainerWeek'],
		}),
		cancelSlot: builder.mutation<
			any,
			{ slotId: string; reason: string; note?: string }
		>({
			query: ({ slotId, ...body }) => ({
				url: `/trainer/timetable/slots/${slotId}/cancel`,
				method: 'POST',
				data: body,
			}),
			invalidatesTags: ['TrainerToday', 'TrainerWeek'],
		}),
		listMyRequests: builder.query<{ requests: any[] }, void>({
			query: () => ({ url: '/trainer/timetable/requests' }),
			providesTags: ['MyRequests'],
		}),
		requestNewEvent: builder.mutation<
			any,
			{
				classId: string;
				/** Extra classes for a clubbed session — approval creates one slot
				 *  per class sharing a bundleId. */
				additionalClassIds?: string[];
				dayBucket: string;
				startTime: string;
				endTime: string;
				reason?: string;
				isLabSlot?: boolean;
			}
		>({
			query: (body) => ({
				url: '/trainer/timetable/requests/new-event',
				method: 'POST',
				data: body,
			}),
			invalidatesTags: ['MyRequests'],
		}),
		requestTimeChange: builder.mutation<
			any,
			{ slotId: string; startTime: string; endTime: string; reason?: string }
		>({
			query: ({ slotId, ...body }) => ({
				url: `/trainer/timetable/slots/${slotId}/request-time-change`,
				method: 'POST',
				data: body,
			}),
			invalidatesTags: ['MyRequests'],
		}),
		getContinuationCandidates: builder.query<{ candidates: any[] }, string>({
			query: (slotId) => ({
				url: `/trainer/timetable/slots/${slotId}/continuation-candidates`,
			}),
			providesTags: ['Continuations'],
		}),
		listNotifications: builder.query<
			{ notifications: TrainerNotification[]; unreadCount: number },
			{ unreadOnly?: boolean } | void
		>({
			query: (arg) => ({
				url: '/trainer/timetable/notifications',
				params: arg && arg.unreadOnly ? { unreadOnly: 'true' } : undefined,
			}),
			providesTags: ['TrainerNotifs'],
		}),
		markNotificationRead: builder.mutation<{ message: string }, string>({
			query: (id) => ({
				url: `/trainer/timetable/notifications/${id}/read`,
				method: 'POST',
			}),
			invalidatesTags: ['TrainerNotifs'],
		}),
		markAllNotificationsRead: builder.mutation<{ message: string }, void>({
			query: () => ({
				url: `/trainer/timetable/notifications/read-all`,
				method: 'POST',
			}),
			invalidatesTags: ['TrainerNotifs'],
		}),
		listSlotMessages: builder.query<{ messages: SlotMessage[] }, string>({
			query: (slotId) => ({
				url: `/trainer/timetable/slots/${slotId}/messages`,
			}),
			providesTags: (_r, _e, slotId) => [{ type: 'SlotMessages', id: slotId }],
		}),
		postSlotMessage: builder.mutation<any, { slotId: string; body: string }>({
			query: ({ slotId, body }) => ({
				url: `/trainer/timetable/slots/${slotId}/messages`,
				method: 'POST',
				data: { body },
			}),
			invalidatesTags: (_r, _e, { slotId }) => [{ type: 'SlotMessages', id: slotId }],
		}),
		getSlotReport: builder.query<ClassReportData, string>({
			query: (slotId) => ({
				url: `/trainer/timetable/slots/${slotId}/report`,
			}),
		}),
		appendSessionNote: builder.mutation<{ ok: boolean }, { slotId: string; note: string }>({
			query: ({ slotId, note }) => ({
				url: `/trainer/timetable/slots/${slotId}/note`,
				method: 'POST',
				data: { note },
			}),
			invalidatesTags: ['TrainerToday', 'TrainerWeek'],
		}),

		// ── Daily Report ───────────────────────────────────────────
		getDayReport: builder.query<DayReportResponse, { date?: string }>({
			query: ({ date }) => ({
				url: '/trainer/timetable/day-report',
				params: date ? { date } : undefined,
			}),
			providesTags: ['DayReport'],
		}),
		uploadReportPhotos: builder.mutation<
			{ ok: boolean; photos: DayReportPhoto[] },
			{ slotId: string; files: File[] }
		>({
			query: ({ slotId, files }) => {
				const form = new FormData();
				files.forEach((f) => form.append('photos', f));
				// Let axios set the multipart boundary itself.
				return {
					url: `/trainer/timetable/slots/${slotId}/report-photos`,
					method: 'POST',
					data: form,
					headers: { 'Content-Type': 'multipart/form-data' },
				};
			},
			invalidatesTags: ['DayReport'],
		}),
		deleteReportPhoto: builder.mutation<
			{ ok: boolean },
			{ slotId: string; photoId: string }
		>({
			query: ({ slotId, photoId }) => ({
				url: `/trainer/timetable/slots/${slotId}/report-photos/${photoId}`,
				method: 'DELETE',
			}),
			invalidatesTags: ['DayReport'],
		}),
		updateSlotRemark: builder.mutation<
			{ ok: boolean; remark: string },
			{ slotId: string; remark: string }
		>({
			query: ({ slotId, remark }) => ({
				url: `/trainer/timetable/slots/${slotId}/remark`,
				method: 'PATCH',
				data: { remark },
			}),
			// Deliberately no invalidation — the remark autosaves on blur while the
			// trainer is still typing in other fields; a refetch would clobber
			// unsaved edits on the rest of the page.
		}),
		submitDayReport: builder.mutation<
			{ ok: boolean; date: string; submittedAt: string },
			{ date?: string }
		>({
			query: ({ date }) => ({
				url: '/trainer/timetable/day-report/submit',
				method: 'POST',
				data: { date },
			}),
			invalidatesTags: ['DayReport'],
		}),
	}),
});

export const {
	useGetTodayQuery,
	useGetWeekQuery,
	useStartSessionMutation,
	useAddMilestoneMutation,
	useEndSessionMutation,
	useSubmitMissedReasonMutation,
	useCancelSlotMutation,
	useListMyRequestsQuery,
	useRequestNewEventMutation,
	useRequestTimeChangeMutation,
	useGetContinuationCandidatesQuery,
	useListNotificationsQuery,
	useMarkNotificationReadMutation,
	useMarkAllNotificationsReadMutation,
	useListSlotMessagesQuery,
	usePostSlotMessageMutation,
	useAppendSessionNoteMutation,
	useGetSlotReportQuery,
	useGetDayReportQuery,
	useUploadReportPhotosMutation,
	useDeleteReportPhotoMutation,
	useUpdateSlotRemarkMutation,
	useSubmitDayReportMutation,
} = trainerTimetableApi;
